/**
 * AIWorkflowChat Component
 *
 * Chat interface for AI-powered workflow creation.
 * Allows users to describe workflows in natural language and
 * receive node suggestions from the AI.
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Send, Loader2, Bot, User, Sparkles, Check, X, RefreshCw, MessageCircle, Bug, ToggleLeft, ToggleRight, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  useWorkflowChatStore,
  WorkflowChatMessage,
  WorkflowSuggestion,
} from '@/stores/workflow/workflow-chat-store';
import {
  workflowChatService,
  Conversation,
} from '@/services/workflow/WorkflowChatService';
import { ChatQuickActions } from './ChatQuickActions';
import { ExecutionStep, ExecutionLog } from '@/stores/devices/device-store';
import {
  buildExecutionContext,
  estimateTokenCount,
  hasExecutionData,
  formatForPreview,
  ExecutionContext,
} from '@/utils/executionLogFormatter';
import { WorkflowEdgeSnapshot, WorkflowNodeSnapshot } from '@/types/workflow-editor';

interface AIWorkflowChatProps {
  /** Current workflow ID for context */
  workflowId?: string;
  /** Current workflow name for context */
  workflowName?: string;
  /** Callback when user accepts a workflow suggestion */
  onAcceptSuggestion?: (suggestion: WorkflowSuggestion) => void;
  /** Callback when nodes should be added to the workflow */
  onAddNodes?: (nodes: WorkflowSuggestion['nodes']) => void;
  /** Additional class names */
  className?: string;
  /** Current nodes in the editor (may differ from saved state) */
  currentNodes?: WorkflowNodeSnapshot[];
  /** Current edges in the editor (may differ from saved state) */
  currentEdges?: WorkflowEdgeSnapshot[];
  /** Execution steps from device store (keyed by executionId or deviceId) */
  executionSteps?: Map<string, ExecutionStep[]>;
  /** Execution logs from device store (keyed by deviceId) */
  executionLogs?: Map<string, ExecutionLog[]>;
  /** Current active execution key (executionId for server, deviceId for ESP32) */
  activeExecutionKey?: string;
  /** Execution source type */
  executionSource?: 'server' | 'esp32';
}

/**
 * Message component for displaying chat messages
 */
const ChatMessageItem: React.FC<{
  message: WorkflowChatMessage;
  isLatest: boolean;
}> = ({ message, isLatest }) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';

  return (
    <div
      className={cn(
        'flex gap-3 p-3 rounded-lg',
        isUser ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-muted/50',
        message.error && 'bg-red-50 dark:bg-red-950/30'
      )}
    >
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser
            ? 'bg-blue-500 text-white'
            : isSystem
            ? 'bg-yellow-500 text-white'
            : 'bg-gradient-to-br from-purple-500 to-blue-500 text-white'
        )}
      >
        {isUser ? (
          <User className="w-4 h-4" />
        ) : isSystem ? (
          <Sparkles className="w-4 h-4" />
        ) : (
          <Bot className="w-4 h-4" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-muted-foreground">
            {isUser ? 'You' : isSystem ? 'System' : 'AI Assistant'}
          </span>
          <span className="text-xs text-muted-foreground">
            {message.timestamp.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
        <div className="text-sm whitespace-pre-wrap break-words">
          {message.content}
        </div>
        {message.isStreaming && isLatest && (
          <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse" />
        )}
        {message.error && (
          <div className="mt-2 text-xs text-red-600 dark:text-red-400">
            Error: {message.error}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Suggestion card for displaying workflow suggestions
 */
const SuggestionCard: React.FC<{
  suggestion: WorkflowSuggestion;
  onAccept: () => void;
  onReject: () => void;
  onSelectOption?: (option: string) => void;
  isLoading?: boolean;
}> = ({ suggestion, onAccept, onReject, onSelectOption, isLoading }) => {
  return (
    <div className="border rounded-lg p-4 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30">
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-sm mb-1">Workflow Suggestion</h4>
          <p className="text-sm text-muted-foreground">{suggestion.description}</p>
        </div>
      </div>

      {/* Node modifications preview */}
      {suggestion.nodeModifications && suggestion.nodeModifications.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Nodes to modify ({suggestion.nodeModifications.length}):
          </p>
          <div className="flex flex-wrap gap-1">
            {suggestion.nodeModifications.map((mod, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800"
                title={mod.changeDescription}
              >
                {mod.newLabel || mod.nodeId}
                {mod.configUpdates && ' (config)'}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Node list preview */}
      {suggestion.nodes.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Nodes to add ({suggestion.nodes.length}):
          </p>
          <div className="flex flex-wrap gap-1">
            {suggestion.nodes.map((node, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded text-xs bg-white dark:bg-gray-800 border"
              >
                {node.name || node.type}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Clarification question */}
      {suggestion.clarificationQuestion && (
        <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded border border-yellow-200 dark:border-yellow-800">
          <p className="text-sm font-medium mb-2">
            {suggestion.clarificationQuestion}
          </p>
          {suggestion.clarificationOptions && (
            <div className="flex flex-wrap gap-2">
              {suggestion.clarificationOptions.map((option, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  className="text-xs hover:bg-purple-100 dark:hover:bg-purple-900/30"
                  onClick={() => onSelectOption?.(option)}
                  disabled={isLoading}
                >
                  {option}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action buttons - show when there are nodes/modifications (even if clarification questions exist) */}
      {(suggestion.nodes.length > 0 || (suggestion.nodeModifications && suggestion.nodeModifications.length > 0)) && (
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={onAccept}
            disabled={isLoading}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            {suggestion.nodeModifications && suggestion.nodeModifications.length > 0 && suggestion.nodes.length > 0
              ? 'Apply Changes'
              : suggestion.nodeModifications && suggestion.nodeModifications.length > 0
              ? 'Apply Modifications'
              : 'Add to Workflow'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReject}
            disabled={isLoading}
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Main AIWorkflowChat component
 */
export const AIWorkflowChat: React.FC<AIWorkflowChatProps> = ({
  workflowId,
  workflowName,
  onAcceptSuggestion,
  onAddNodes,
  className,
  currentNodes,
  currentEdges,
  executionSteps,
  executionLogs,
  activeExecutionKey,
  executionSource = 'server',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [includeExecutionLogs, setIncludeExecutionLogs] = useState(false);

  // Build execution context from provided data
  const executionContext = useMemo((): ExecutionContext | null => {
    if (!includeExecutionLogs || !activeExecutionKey) return null;

    const steps = executionSteps?.get(activeExecutionKey) || [];
    const logs = executionLogs?.get(activeExecutionKey) || [];

    if (steps.length === 0 && logs.length === 0) return null;

    return buildExecutionContext(
      executionSource,
      activeExecutionKey,
      steps,
      logs,
      workflowId
    );
  }, [includeExecutionLogs, activeExecutionKey, executionSteps, executionLogs, executionSource, workflowId]);

  // Check if we have execution data available
  const hasExecutionDataAvailable = useMemo(() => {
    if (!activeExecutionKey) return false;
    const steps = executionSteps?.get(activeExecutionKey) || [];
    const logs = executionLogs?.get(activeExecutionKey) || [];
    return steps.length > 0 || logs.length > 0;
  }, [activeExecutionKey, executionSteps, executionLogs]);

  // Preview of what will be sent
  const executionPreview = useMemo(() => {
    if (!executionContext) return '';
    return formatForPreview(executionContext);
  }, [executionContext]);

  // Estimated token count
  const estimatedTokens = useMemo(() => {
    if (!executionContext) return 0;
    return estimateTokenCount(executionContext);
  }, [executionContext]);

  const {
    messages,
    inputValue,
    isLoading,
    isTyping,
    error,
    pendingSuggestion,
    setInputValue,
    setError,
    setIsLoading,
    addMessage,
    updateMessage,
    setPendingSuggestion,
    acceptSuggestion,
    rejectSuggestion,
    initialize,
    setWorkflowContext,
    clearMessages,
  } = useWorkflowChatStore();

  // Initialize chat when mounted
  useEffect(() => {
    initialize(workflowId);
    setWorkflowContext(workflowId || null, workflowName || null);
  }, [workflowId, workflowName, initialize, setWorkflowContext]);

  // Create or load conversation
  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (conversationId) return conversationId;

    setIsInitializing(true);
    try {
      // Try to get existing conversations for this workflow
      const response = await workflowChatService.listConversations({
        workflowId,
        limit: 1,
      });

      if (response.success && response.data && response.data.length > 0) {
        // Use existing conversation
        const conv = response.data[0];
        setConversationId(conv._id);

        // Load existing messages
        const fullConv = await workflowChatService.getConversation(conv._id);
        if (fullConv.success && fullConv.data?.messages) {
          clearMessages();
          fullConv.data.messages.forEach((msg) => {
            addMessage({
              id: msg.id,
              content: msg.content,
              role: msg.role,
              timestamp: new Date(msg.timestamp),
              workflowSuggestion: msg.workflowSuggestion,
            });
          });
        }

        return conv._id;
      } else {
        // Create new conversation
        const createResponse = await workflowChatService.createConversation({
          title: workflowName ? `Chat: ${workflowName}` : 'New Workflow Chat',
          workflowId,
        });

        if (createResponse.success && createResponse.data) {
          setConversationId(createResponse.data._id);
          return createResponse.data._id;
        }
      }
    } catch (err: unknown) {
      console.error('Failed to initialize conversation:', err);

      // Provide more helpful error messages
      let errorMessage = 'Failed to initialize conversation';
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (axios.isAxiosError(err) && err.response?.status === 403) {
        errorMessage = 'You do not have permission to access chat.';
      } else if (
        axios.isAxiosError(err) &&
        (err.code === 'ERR_NETWORK' || err.message.includes('Network Error'))
      ) {
        errorMessage = 'Cannot connect to workflow service. Is it running?';
      } else if (axios.isAxiosError(err) && typeof err.response?.data?.message === 'string') {
        errorMessage = err.response.data.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsInitializing(false);
    }

    return null;
  }, [conversationId, workflowId, workflowName, clearMessages, addMessage, setError]);

  // Start a new conversation
  const handleNewConversation = useCallback(async () => {
    try {
      const response = await workflowChatService.createConversation({
        title: workflowName ? `Chat: ${workflowName}` : 'New Workflow Chat',
        workflowId,
      });
      if (response.success && response.data) {
        setConversationId(response.data._id);
        clearMessages();
        addMessage({
          id: `system-${Date.now()}`,
          content: "Hello! I can help you build workflows. Describe what you want your workflow to do, and I'll suggest the nodes to create it.",
          role: 'assistant',
          timestamp: new Date(),
        });
      }
    } catch (err) {
      console.error('Failed to create new conversation:', err);
    }
  }, [workflowId, workflowName, clearMessages, addMessage]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pendingSuggestion]);

  // Handle sending a message
  const handleSend = useCallback(async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    setInputValue('');
    setIsLoading(true);
    setError(null);

    // Ensure we have a conversation
    const convId = await ensureConversation();
    if (!convId) {
      setError('Failed to initialize conversation');
      setIsLoading(false);
      return;
    }

    // Add optimistic user message
    const userMsg = addMessage({
      content: trimmedInput,
      role: 'user',
    });

    // Add placeholder assistant message for loading state
    const assistantMessage = addMessage({
      content: '',
      role: 'assistant',
      isStreaming: true,
    });

    try {
      // Call the backend API, optionally including execution context for debugging
      const response = await workflowChatService.sendMessage(convId, trimmedInput, {
        executionContext: executionContext || undefined,
        currentNodes,
        currentEdges,
      });

      if (response.success && response.data) {
        const { assistantMessage: apiAssistantMessage } = response.data;

        // Update assistant message with actual response
        updateMessage(assistantMessage.id, {
          content: apiAssistantMessage.content,
          isStreaming: false,
          workflowSuggestion: apiAssistantMessage.workflowSuggestion,
        });

        // If there's a workflow suggestion, set it as pending
        if (apiAssistantMessage.workflowSuggestion) {
          setPendingSuggestion(apiAssistantMessage.workflowSuggestion);
        }
      } else {
        throw new Error(response.message || 'Failed to get response');
      }
    } catch (err) {
      console.error('Chat error:', err);
      updateMessage(assistantMessage.id, {
        content: 'Sorry, I encountered an error processing your request.',
        isStreaming: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      setError('Failed to get response');
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, addMessage, setInputValue, setIsLoading, setError, updateMessage, ensureConversation, setPendingSuggestion, executionContext]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Handle accepting a suggestion
  const handleAcceptSuggestion = useCallback(() => {
    if (!pendingSuggestion) return;

    setIsApplying(true);

    try {
      const suggestion = acceptSuggestion();
      if (suggestion) {
        // Notify parent component - prefer onAcceptSuggestion as it handles full suggestion
        // including edges, edgesToRemove, and nodeModifications. Only use onAddNodes as fallback.
        if (onAcceptSuggestion) {
          onAcceptSuggestion(suggestion);
        } else {
          onAddNodes?.(suggestion.nodes);
        }

        // Build confirmation message based on what was changed
        const modCount = suggestion.nodeModifications?.length || 0;
        const nodeCount = suggestion.nodes.length;
        let confirmMessage = '';

        if (modCount > 0 && nodeCount > 0) {
          confirmMessage = `Modified ${modCount} node(s) and added ${nodeCount} node(s) to your workflow.`;
        } else if (modCount > 0) {
          confirmMessage = `Modified ${modCount} node(s) in your workflow.`;
        } else {
          confirmMessage = `Added ${nodeCount} node(s) to your workflow.`;
        }

        addMessage({
          content: confirmMessage,
          role: 'assistant',
        });
      }
    } catch (err) {
      console.error('Failed to apply suggestion:', err);
      setError('Failed to apply suggestion');
    } finally {
      setIsApplying(false);
    }
  }, [pendingSuggestion, acceptSuggestion, onAcceptSuggestion, onAddNodes, addMessage, setError]);

  // Handle rejecting a suggestion
  const handleRejectSuggestion = useCallback(() => {
    rejectSuggestion();
  }, [rejectSuggestion]);

  // Handle quick action click
  const handleQuickAction = useCallback((message: string) => {
    setInputValue(message);
    // Auto-focus the input
    inputRef.current?.focus();
  }, [setInputValue]);

  // Handle selecting a clarification option
  const handleSelectOption = useCallback(async (option: string) => {
    // Clear the pending suggestion since user is responding
    setPendingSuggestion(null);

    // Set the selected option as input and send it
    setInputValue(option);

    // Trigger send with the selected option
    setIsLoading(true);
    setError(null);

    const convId = await ensureConversation();
    if (!convId) {
      setError('Failed to initialize conversation');
      setIsLoading(false);
      return;
    }

    // Add user message with selected option
    addMessage({
      content: option,
      role: 'user',
    });

    // Add placeholder assistant message
    const assistantMessage = addMessage({
      content: '',
      role: 'assistant',
      isStreaming: true,
    });

    try {
      const response = await workflowChatService.sendMessage(convId, option, {
        executionContext: executionContext || undefined,
        currentNodes,
        currentEdges,
      });

      if (response.success && response.data) {
        const { assistantMessage: apiAssistantMessage } = response.data;

        updateMessage(assistantMessage.id, {
          content: apiAssistantMessage.content,
          isStreaming: false,
          workflowSuggestion: apiAssistantMessage.workflowSuggestion,
        });

        if (apiAssistantMessage.workflowSuggestion) {
          setPendingSuggestion(apiAssistantMessage.workflowSuggestion);
        }
      } else {
        throw new Error(response.message || 'Failed to get response');
      }
    } catch (err) {
      console.error('Chat error:', err);
      updateMessage(assistantMessage.id, {
        content: 'Sorry, I encountered an error processing your request.',
        isStreaming: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      setError('Failed to get response');
    } finally {
      setIsLoading(false);
      setInputValue('');
    }
  }, [ensureConversation, addMessage, updateMessage, setIsLoading, setError, setInputValue, setPendingSuggestion, executionContext]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Chat header */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <span className="text-xs text-muted-foreground">AI Assistant</span>
        <button
          onClick={handleNewConversation}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          title="Start new conversation"
        >
          <PlusCircle className="h-3.5 w-3.5" />
          New Chat
        </button>
      </div>
      {/* Chat messages area */}
      <ScrollArea ref={scrollRef} className="flex-1 p-4">
        <div className="space-y-3">
          {messages.map((message, index) => (
            <ChatMessageItem
              key={message.id}
              message={message}
              isLatest={index === messages.length - 1}
            />
          ))}

          {/* Pending suggestion */}
          {pendingSuggestion && (
            <SuggestionCard
              suggestion={pendingSuggestion}
              onAccept={handleAcceptSuggestion}
              onReject={handleRejectSuggestion}
              onSelectOption={handleSelectOption}
              isLoading={isApplying || isLoading}
            />
          )}

          {/* Loading indicator */}
          {isLoading && !messages.find((m) => m.isStreaming) && (
            <div className="flex items-center gap-2 p-3 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Thinking...</span>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Error message */}
      {error && (
        <div className="px-4 py-2 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Input area */}
      <div className="border-t p-4 space-y-3">
        {/* Quick actions - show when getting started */}
        {messages.filter(m => m.role === 'user').length < 2 && !isLoading && !pendingSuggestion && (
          <ChatQuickActions
            onActionClick={handleQuickAction}
            compact
            disabled={isLoading}
          />
        )}

        {/* Debug logs toggle - only show if execution data is available */}
        {hasExecutionDataAvailable && (
          <div className="flex items-center justify-between px-1">
            <button
              type="button"
              onClick={() => setIncludeExecutionLogs(!includeExecutionLogs)}
              className={cn(
                'flex items-center gap-2 text-xs rounded-md px-2 py-1 transition-colors',
                includeExecutionLogs
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                  : 'text-muted-foreground hover:bg-muted'
              )}
            >
              <Bug className="w-3.5 h-3.5" />
              <span>Include debug logs</span>
              {includeExecutionLogs ? (
                <ToggleRight className="w-4 h-4 text-amber-600" />
              ) : (
                <ToggleLeft className="w-4 h-4" />
              )}
            </button>
            {includeExecutionLogs && executionPreview && (
              <span className="text-xs text-muted-foreground">
                {executionPreview} (~{estimatedTokens} tokens)
              </span>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you want your workflow to do..."
            className={cn(
              'flex-1 min-h-[44px] max-h-[120px] resize-none rounded-lg border border-input',
              'bg-background px-3 py-2 text-sm shadow-sm',
              'placeholder:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-50'
            )}
            disabled={isLoading}
            rows={1}
          />
          <Button
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            size="icon"
            className="h-[44px] w-[44px]"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default AIWorkflowChat;
